import PropTypes from "prop-types"
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useForm } from "react-hook-form"
import { useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import CardReaderProcessing from "src/main/Billing/Payments/CardReaderProcessing"
import useCardReaderPayment from "src/main/Billing/Payments/useCardReaderPayment"

import Button from "src/components/Button"
import Form from "src/components/Form"
import LineItems from "src/components/LineItems/LineItems"
import Modal from "src/components/Modal"

import { createContactPaymentTxn } from "src/api/Billing/Payments"

import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import DialogFooter from "./DialogFooter"
import DialogHeader from "./DialogHeader"
import InvoicesTable from "./InvoicesTable"
import PaymentMethodFields from "./PaymentMethodFields"
import {
  CARD_READER_PROCESSED,
  CARD_READER_SELECTED,
  PAY_INVOICES_CANCELLED,
  PAY_INVOICES_CONFIRMED,
  PAY_INVOICES_OPENED,
} from "./amplitude_events"
import { countedCopy, fillCopy } from "./copy"
import {
  paymentPreviewKey,
  useCreateContactPayment,
  usePaymentPreview,
} from "./hooks"
import {
  CARD_READER_METHOD,
  MANUAL_PAYMENT_METHOD,
  PAYMENT_PAID,
  PAYMENT_PROCESSING,
  SAVED_METHOD,
  buildTxn,
  selectedMethod,
  trackedMethod,
} from "./methods"

const SUMMARY_ROW_TYPES = ["subtotal", "tax", "horizontal_rule", "total"]

const failureFrom = (error, fallbackMessage) => ({
  message: error?.errors?.[0] || fallbackMessage,
  invoiceId: error?.invoiceId,
  paymentPath: error?.paymentPath,
})

const PayInvoicesDialog = ({
  invoices,
  contact,
  contactId,
  entryPoint,
  copy,
  cardReaders = [],
  enableBluetoothCardReader = false,
  onPaid,
  onClose,
}) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const tracker = useTracker()
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState(null)
  const needsRefreshRef = useRef(false)
  const hasPickedMethodRef = useRef(false)
  const [hasSettledFirstPreview, setHasSettledFirstPreview] = useState(false)
  const summaryHeadingId = useId()
  const invoiceIds = useMemo(
    () => invoices.map((invoice) => invoice.id),
    [invoices]
  )

  const formatMoney = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })

  const offeredCardReaders = invoices.length === 1 ? cardReaders : []
  const { control, register, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      paymentMethodId: "",
      paymentType: "cash",
      cardReaderId: offeredCardReaders[0]
        ? String(offeredCardReaders[0].id)
        : "",
    },
  })
  const [paymentMethodId, paymentType, selectedCardReaderId] = watch([
    "paymentMethodId",
    "paymentType",
    "cardReaderId",
  ])
  const method = selectedMethod({
    paymentMethodId,
    paymentType,
    cardReaderId: selectedCardReaderId,
  })
  const isCardReader = method.kind === CARD_READER_METHOD
  const cardReaderId = isCardReader
    ? offeredCardReaders.find(({ id }) => String(id) === method.id)?.id
    : null

  const previewParams = {
    contactId,
    invoiceIds,
    paymentMethodId: method.kind === SAVED_METHOD ? Number(method.id) : null,
    cardPresent: isCardReader,
  }
  const {
    data: preview,
    error: previewError,
    isFetching: isFetchingPreview,
  } = usePaymentPreview({ marinaSlug, ...previewParams })
  const [paymentMethods, setPaymentMethods] = useState([])
  useEffect(() => {
    if (preview) {
      setPaymentMethods(preview.paymentMethods)
    }
  }, [preview])

  const eventProperties = {
    entryPoint,
    invoiceCount: invoices.length,
    method: trackedMethod({ method, paymentMethods }),
  }

  const showFailure = (error, fallbackMessage) => {
    if (error?.status === 409 && error.preview) {
      queryClient.setQueryData(paymentPreviewKey(previewParams), {
        ...error.preview,
        paymentMethods,
      })
    }
    needsRefreshRef.current = true
    setFailure(failureFrom(error, fallbackMessage))
  }

  const { mutate: createPayment, isLoading: isSubmitting } =
    useCreateContactPayment({
      marinaSlug,
      contactId,
      onError: (error) => showFailure(error, copy.outcome.failed),
    })

  const cardReaderPayment = useCardReaderPayment({
    invoiceId: invoiceIds[0],
    marinaSlug,
    cardReaderId,
    enableBluetoothCardReader,
    isSelected: isCardReader,
    events: {
      selected: CARD_READER_SELECTED,
      processed: CARD_READER_PROCESSED,
    },
    submit: (data) => createContactPaymentTxn({ marinaSlug, contactId, data }),
    onSettled: () => onPaid(PAYMENT_PAID),
    onError: (message, error) => showFailure(error, message),
    genericErrorMessage: copy.outcome.failed,
    copy: copy.cardReader,
  })

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      if (
        type === "change" &&
        (name === "paymentMethodId" || name === "paymentType")
      ) {
        hasPickedMethodRef.current = true
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  // A layout effect, so the picker is never painted blank between the first
  // preview landing and the default method being set.
  useLayoutEffect(() => {
    if (hasSettledFirstPreview || !(preview || previewError)) {
      return
    }
    if (!hasPickedMethodRef.current) {
      setValue(
        "paymentMethodId",
        preview?.paymentMethods[0]?.id ?? MANUAL_PAYMENT_METHOD
      )
    }
    setHasSettledFirstPreview(true)
  }, [hasSettledFirstPreview, preview, previewError, setValue])

  useEffect(() => {
    if (hasSettledFirstPreview) {
      tracker.trackEvent(PAY_INVOICES_OPENED, eventProperties)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSettledFirstPreview])

  useEffect(() => {
    setFailure(null)
  }, [method.kind, method.id])

  useEffect(() => {
    if (previewError) {
      needsRefreshRef.current = true
    }
  }, [previewError])

  const closeDialog = () => onClose({ needsRefresh: needsRefreshRef.current })

  const shownFailure =
    failure ?? (previewError && failureFrom(previewError, copy.outcome.failed))
  const lines =
    preview?.invoices ??
    invoices.map((invoice) => ({
      invoiceId: invoice.id,
      balanceCents: invoice.balance,
      sourceType: invoice.sourceType,
      dueDate: invoice.dueDate,
    }))
  const amountDue =
    preview?.balanceCents ??
    invoices.reduce((sum, invoice) => sum + invoice.balance, 0)
  const isRowFailure = lines.some(
    (line) => line.invoiceId === shownFailure?.invoiceId
  )
  const isSummaryLoading = isFetchingPreview || !hasSettledFirstPreview
  const confirmTemplate = {
    [SAVED_METHOD]: copy.buttons.charge,
    [CARD_READER_METHOD]: copy.buttons.collectOnReader,
  }[method.kind]
  const confirmText = fillCopy(confirmTemplate ?? copy.buttons.recordPayment, {
    amount: formatMoney(preview?.totalCents ?? amountDue),
  })

  const confirm = handleSubmit((values) => {
    tracker.trackEvent(PAY_INVOICES_CONFIRMED, eventProperties)
    setFailure(null)
    const data = {
      invoiceIds,
      confirmedTotal: preview.totalCents,
      txn: buildTxn({ method, values, cardReaderId }),
    }
    if (isCardReader) {
      needsRefreshRef.current = true
      cardReaderPayment.start(data)
    } else {
      const isSavedMethod = method.kind === SAVED_METHOD
      createPayment(data, {
        onSuccess: ({ data: payment }) =>
          onPaid(
            isSavedMethod && payment?.displayStatus === "Processing"
              ? PAYMENT_PROCESSING
              : PAYMENT_PAID
          ),
      })
    }
  })

  const isPaying =
    isSubmitting ||
    cardReaderPayment.isStarting ||
    cardReaderPayment.isCollecting

  const close = () => {
    if (isPaying) {
      return
    }
    if (hasSettledFirstPreview) {
      tracker.trackEvent(PAY_INVOICES_CANCELLED, eventProperties)
    }
    closeDialog()
  }

  const renderFooter = () => {
    if (cardReaderPayment.isCollecting) {
      return (
        <Modal.Footer>
          <div className="flex justify-end">
            <Button
              variant="tertiary"
              onClick={() => cardReaderPayment.cancel()}
              isLoading={cardReaderPayment.isCanceling}
            >
              {copy.buttons.cancelPayment}
            </Button>
          </div>
        </Modal.Footer>
      )
    }
    if (cardReaderPayment.isTimedOut) {
      return (
        <Modal.Footer>
          <div className="flex justify-end">
            <Button variant="primary" onClick={closeDialog}>
              {copy.buttons.close}
            </Button>
          </div>
        </Modal.Footer>
      )
    }
    return (
      <DialogFooter
        cancelText={copy.buttons.cancel}
        onCancel={close}
        isCancelDisabled={isPaying}
        confirmText={confirmText}
        onConfirm={confirm}
        isConfirmDisabled={
          !hasSettledFirstPreview || !preview || isFetchingPreview
        }
        isConfirmLoading={isSubmitting || cardReaderPayment.isStarting}
        hasShadow={false}
      />
    )
  }

  const header = (
    <DialogHeader
      title={countedCopy(copy.title, invoices.length)}
      subtitle={fillCopy(copy.subtitle, {
        name: contact.name,
        amount: formatMoney(amountDue),
      })}
    />
  )

  return (
    <Modal
      isOpen
      onClose={close}
      size="mediumFixed"
      dismissable={!cardReaderPayment.isCollecting}
    >
      {cardReaderPayment.isProcessingView ? (
        <>
          {header}
          <Modal.Body>
            <CardReaderProcessing
              isCollecting={cardReaderPayment.isCollecting}
              cardReaders={offeredCardReaders}
              cardReaderId={cardReaderId}
              errorMessage={failure?.message}
              copy={copy.cardReader}
            />
          </Modal.Body>
        </>
      ) : (
        // In place of Modal.Body, so this column is the flex item that sizes
        // to the dialog: 720 px makes Figma's 820 px dialog with the Modal's
        // padding and the footer, the desktop dialog's max height shrinks it
        // on shorter screens, and the touch sheet's set height grows it.
        // Below the scroll area's 120 px minimum the whole column scrolls;
        // -mx-4 px-4 keeps the summary's full-width shadow inside its clip.
        <div className="-mx-4 mb-3 flex min-h-0 grow basis-[720px] flex-col overflow-y-auto px-4">
          {header}
          <div
            data-testid="pay-invoices-scroll-area"
            className="-mx-1 min-h-[120px] flex-1 overflow-y-auto px-1"
          >
            {/* The top space is padding on this inner box, not on the
                scroll area, which would hold the sticky header 32 px down. */}
            <div className="pb-6 pt-8">
              <PaymentMethodFields
                control={control}
                register={register}
                method={method}
                paymentMethods={paymentMethods}
                cardReaders={offeredCardReaders}
                copy={copy}
              />
              <h5 className="m-0 mb-2 mt-3 text-sm font-semibold text-gray-800">
                {copy.sections.paymentPreview}
              </h5>
              <InvoicesTable
                lines={lines}
                copy={copy.table}
                formatMoney={formatMoney}
                failure={shownFailure}
                viewPaymentLabel={copy.outcome.viewPayment}
              />
            </div>
          </div>
          <section
            className="relative -mx-4 px-4 pt-6 shadow-top-md"
            aria-labelledby={summaryHeadingId}
            aria-busy={isSummaryLoading}
          >
            <h5
              id={summaryHeadingId}
              className="m-0 mb-2 text-sm font-semibold text-gray-800"
            >
              {copy.sections.paymentSummary}
            </h5>
            {(preview || isSummaryLoading) && (
              <div
                data-testid="payment-summary-rows"
                className="rounded-lg border px-4 py-2"
              >
                <LineItems
                  lineItems={preview?.lineItems}
                  loading={isSummaryLoading}
                  loadingTypes={SUMMARY_ROW_TYPES}
                  currency={presentmentCurrency}
                />
              </div>
            )}
            {isCardReader && (
              <p className="text-muted m-0 mt-6">{copy.summary.readerNote}</p>
            )}
            {shownFailure && !isRowFailure && (
              <div className="mt-6">
                <Form.Error>{shownFailure.message}</Form.Error>
                {shownFailure.paymentPath && (
                  <a href={shownFailure.paymentPath}>
                    {copy.outcome.viewPayment}
                  </a>
                )}
              </div>
            )}
          </section>
        </div>
      )}
      {renderFooter()}
    </Modal>
  )
}

PayInvoicesDialog.propTypes = {
  invoices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      balance: PropTypes.number.isRequired,
      sourceType: PropTypes.string,
      dueDate: PropTypes.string,
      status: PropTypes.string,
    })
  ).isRequired,
  contact: PropTypes.shape({ name: PropTypes.string }).isRequired,
  contactId: PropTypes.string.isRequired,
  entryPoint: PropTypes.string.isRequired,
  copy: PropTypes.object.isRequired,
  cardReaders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      displayName: PropTypes.string,
    })
  ),
  enableBluetoothCardReader: PropTypes.bool,
  onPaid: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default PayInvoicesDialog
